import Flex from 'components/ui/Flex';
import Text from 'components/ui/Text';
import Spinner from 'components/ui/Spinner';
import ActionButton from 'components/ui/ActionButton';
import Input from 'components/ui/Input';
import LogItem from './LogItem';
import { useTranslation } from 'react-i18next';
import { ReturnLogAction } from 'returnCenter-api';
import { styled } from 'styled-components';
import { useState } from 'react';
import useAppDispatch from 'hooks/useAppDispatch';
import useAppSelector from 'hooks/useAppSelector';
import { returnSelectors, returnThunks } from 'store/reducers/returnSlice';
import { useEffect } from 'react';
import { faPlus } from '@fortawesome/pro-light-svg-icons';

// Filter options for log actions
type LogFilterOption = 'all' | 'comments' | 'statusChanges' | 'itemChanges';

const ReturnLogs = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const returnSlipId = useAppSelector(returnSelectors.selectReturnId);
    const unsortedLogs = useAppSelector(returnSelectors.selectReturnLogs);

    const [newComment, setNewComment] = useState('');
    const [isExpanded, setIsExpanded] = useState(false);
    const [activeFilter, setActiveFilter] = useState<LogFilterOption>('all');

    // Filter logs based on selected filter
    const filterLogs = (logsToFilter: typeof unsortedLogs) => {
        if (activeFilter === 'all') return logsToFilter;

        return logsToFilter.filter((log) => {
            switch (activeFilter) {
                case 'comments':
                    return (
                        log.action === ReturnLogAction.InternalComment || log.action === ReturnLogAction.ExternalComment
                    );
                case 'statusChanges':
                    return log.action === ReturnLogAction.StatusChanged;
                case 'itemChanges':
                    return (
                        log.action === ReturnLogAction.ItemsAdded ||
                        log.action === ReturnLogAction.ItemsRemoved ||
                        log.action === ReturnLogAction.ItemsModified ||
                        log.action === ReturnLogAction.CreditCreated
                    );
                default:
                    return true;
            }
        });
    };

    // Keep logs in original order (by date from API), apply filter
    const logs = filterLogs(unsortedLogs);

    // Calculate counts for each filter type
    const commentCount = unsortedLogs.filter(
        (log) => log.action === ReturnLogAction.InternalComment || log.action === ReturnLogAction.ExternalComment,
    ).length;

    const statusChangeCount = unsortedLogs.filter((log) => log.action === ReturnLogAction.StatusChanged).length;

    const itemChangeCount = unsortedLogs.filter(
        (log) =>
            log.action === ReturnLogAction.ItemsAdded ||
            log.action === ReturnLogAction.ItemsRemoved ||
            log.action === ReturnLogAction.ItemsModified ||
            log.action === ReturnLogAction.CreditCreated,
    ).length;

    // Get pinned logs sorted by date (newest first) to show above input (not filtered)
    const pinnedLogs = [...unsortedLogs]
        .filter((log) => log.pinned)
        .sort((a, b) => {
            const dateA = a.date ? new Date(a.date).getTime() : 0;
            const dateB = b.date ? new Date(b.date).getTime() : 0;
            return dateB - dateA;
        });

    const loading = useAppSelector(returnSelectors.selectReturnLogsLoading);
    const error = useAppSelector(returnSelectors.selectReturnLogsError);

    useEffect(() => {
        if (returnSlipId) {
            dispatch(returnThunks.getReturnLogs(returnSlipId));
        }
    }, [returnSlipId, dispatch]);

    const handleAddLog = () => {
        if (!returnSlipId || !newComment.trim()) return;

        dispatch(returnThunks.addReturnLog({ returnId: returnSlipId, comment: newComment.trim() }));
        setNewComment('');
    };

    const handleTogglePin = (logId: number | undefined, pinned: boolean) => {
        if (!returnSlipId || logId === undefined) return;

        dispatch(returnThunks.toggleReturnLogPinned({ logId, pinned, returnId: returnSlipId }));
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleAddLog();
        }
    };

    if (!returnSlipId) {
        return null;
    }

    return (
        <ContainerWithRadius column gap="4px" className="return-logs-wrapper">
            <Text as="label" margin={0} padding={0} fontSize={11} xBold>
                {t('returnLogs')} ({logs.length})
            </Text>

            {/* Filter buttons */}
            <FilterBar gap="0.5rem">
                <FilterButton $active={activeFilter === 'all'} onClick={() => setActiveFilter('all')}>
                    {t('filter.all')}
                </FilterButton>
                {commentCount > 0 && (
                    <FilterButton $active={activeFilter === 'comments'} onClick={() => setActiveFilter('comments')}>
                        {t('filter.comments')} ({commentCount})
                    </FilterButton>
                )}
                {statusChangeCount > 0 && (
                    <FilterButton
                        $active={activeFilter === 'statusChanges'}
                        onClick={() => setActiveFilter('statusChanges')}
                    >
                        {t('filter.statusChanges')} ({statusChangeCount})
                    </FilterButton>
                )}
                {itemChangeCount > 0 && (
                    <FilterButton
                        $active={activeFilter === 'itemChanges'}
                        onClick={() => setActiveFilter('itemChanges')}
                    >
                        {t('filter.itemChanges')} ({itemChangeCount})
                    </FilterButton>
                )}
            </FilterBar>

            <Wrapper column gap="0.5rem" $grow={1}>
                {/* Pinned logs shown at the top */}
                {pinnedLogs.length > 0 && (
                    <PinnedSection column gap="0.5rem">
                        <Text fontSize={11} bold color="main">
                            {t('pinnedLogs')} ({pinnedLogs.length})
                        </Text>
                        <LogList column gap="0.5rem">
                            {pinnedLogs.map((log) => (
                                <LogItem
                                    key={`pinned-${log.id}`}
                                    log={log}
                                    t={t}
                                    loading={loading}
                                    onTogglePin={handleTogglePin}
                                />
                            ))}
                        </LogList>
                    </PinnedSection>
                )}

                <AddLogSection gap="0.5rem" column>
                    <StyledInput
                        type="text"
                        placeholder={t('addLogComment')}
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        onKeyDown={handleKeyDown}
                        disabled={loading}
                    />
                    <ActionButton
                        label={t('addLog')}
                        onClick={handleAddLog}
                        disabled={loading || !newComment.trim()}
                        isLoading={loading}
                        icon={faPlus}
                        rounded
                        main
                        fullWidth
                    />
                </AddLogSection>

                {error && (
                    <ErrorText fontSize={12} color="error">
                        {error}
                    </ErrorText>
                )}

                {loading && logs.length === 0 ? (
                    <Flex justify="center" className="mt-2">
                        <Spinner size="small" color="main" />
                    </Flex>
                ) : logs.length === 0 ? (
                    <NoLogsText fontSize={14} italic>
                        {t('noLogsAvailable')}
                    </NoLogsText>
                ) : (
                    <>
                        {/* First log always visible */}
                        <LogList column gap="0.5rem">
                            <LogItem log={logs[0]} t={t} loading={loading} onTogglePin={handleTogglePin} />
                        </LogList>

                        {/* Toggle for remaining logs */}
                        {logs.length > 1 && (
                            <>
                                <Header justify="between" align="center">
                                    <ToggleButton onClick={() => setIsExpanded(!isExpanded)}>
                                        {isExpanded ? t('hide') : t('show')} ({logs.length - 1})
                                    </ToggleButton>
                                </Header>

                                {isExpanded && (
                                    <LogList column gap="0.5rem">
                                        {logs.slice(1).map((log) => (
                                            <LogItem
                                                key={log.id}
                                                log={log}
                                                t={t}
                                                loading={loading}
                                                onTogglePin={handleTogglePin}
                                            />
                                        ))}
                                    </LogList>
                                )}
                            </>
                        )}
                    </>
                )}
            </Wrapper>
        </ContainerWithRadius>
    );
};

export default ReturnLogs;

const ContainerWithRadius = styled(Flex)`
    border-radius: var(--radius);
`;

const Wrapper = styled(Flex)`
    background-color: ${({ theme }) => theme.colors.fillWhite};
    padding: 0.5rem;
    border-radius: var(--radius);
`;

const Header = styled(Flex)``;

const PinnedSection = styled(Flex)`
    background-color: ${({ theme }) => theme.colors.fill};
    padding: 0.5rem;
    border-radius: var(--radius);
    border-left: 3px solid ${({ theme }) => theme.colors.main};
`;

const ToggleButton = styled.button`
    background: none;
    border: none;
    color: ${({ theme }) => theme.colors.main};
    cursor: pointer;
    font-size: 12px;
    font-weight: 600;
    padding: 0.5rem;

    &:hover {
        text-decoration: underline;
    }
`;

const AddLogSection = styled(Flex)`
    margin-top: 0rem;
`;

const StyledInput = styled(Input)`
    flex: 1;

    input {
        border: 1px solid ${({ theme }) => theme.colors.border};
        border-radius: var(--radius);
    }
`;

const LogList = styled(Flex)``;

const FilterBar = styled(Flex)`
    margin-bottom: 0.5rem;
`;

const FilterButton = styled.button<{ $active?: boolean }>`
    background-color: ${({ theme, $active }) => ($active ? theme.colors.main : 'transparent')};
    color: ${({ theme, $active }) => ($active ? theme.colors.fillWhite : theme.colors.main)};
    border: 1px solid ${({ theme }) => theme.colors.main};
    border-radius: var(--radius);
    padding: 4px 8px;
    font-size: 10px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background-color: ${({ theme, $active }) => ($active ? theme.colors.main : theme.colors.fill)};
    }
`;

const NoLogsText = styled(Text)`
    text-align: center;
    padding: 1rem;
    color: ${({ theme }) => theme.colors.textFaded};
`;

const ErrorText = styled(Text)`
    color: ${({ theme }) => theme.colors.error};
`;
